import React, { memo, useCallback } from 'react';
import {
  ToolbarClearIcons,
  ToolbarSearchIcons,
} from '../../../../../../icons/icons';
import { ExportButton } from './ExportButton';
import { GridViewControls } from './GridViewControls';
import { ToolbarProps } from './interfaces';
import { RefreshButton } from './RefreshButton';
import { TableViewControls } from './TableViewControls';
import { UnsuccessfulApplicantsActions } from './UnsuccessfulApplicantsActions';
import { ViewModeToggle } from './ViewModeToggle';

export const Toolbar = memo<ToolbarProps>((props) => {
  const {
    globalSearchTerm,
    setGlobalSearchTerm,
    viewMode,
    setViewMode,
    isAnalyticalFilter,
    is3dView,
    svgActiveTab,
    setSvgActiveTab,
    view3D,
    setView3D,
    loading,
    currentTab,
    selectAllUnsuccessful,
    handleColumnConfig,
    handleClearSelected,
    isClearDisabled,
    handleSendEmailToSelected,
    isSendEmailDisabled,
    selectedApplicantCount,
    handleExport,
    filteredCandidateCount,
    candidateCount,
    handleAnalyticsFilterOpen,
    isAnalyticsFilterOpen,
    handleToggle3DView,
    handleGraphConfig,
    handleRefreshData,
    showHeadshots,
    setShowHeadshots,
    handleStatusChange,
    handlePreQualFilterOpen,
    isPreQualFilterOpen,
    handleCCAFilterOpen,
    isCCAFilterOpen,
  } = props;

  const clearSearchTerm = useCallback(() => {
    setGlobalSearchTerm('');
  }, [setGlobalSearchTerm]);

  const toggleSvgTab = useCallback(() => {
    setSvgActiveTab(svgActiveTab === 3 ? -1 : 3);
  }, [setSvgActiveTab, svgActiveTab]);

  const toggle3DViewWithReset = useCallback(() => {
    setView3D(!view3D);
    setSvgActiveTab(-1);
  }, [setView3D, view3D, setSvgActiveTab]);

  const handleHeadshotToggle = useCallback(() => {
    setShowHeadshots(!showHeadshots);
  }, [setShowHeadshots, showHeadshots]);

  const isUnsuccessfulTab = currentTab === 'Unsuccessful Applicants';
  return (
    <div className='sticky top-0 z-20 rounded-lg border border-slate-200/70 bg-white/95 shadow-sm backdrop-blur-md'>
      <style>{ `
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      ` }</style>
      { /* Unified Ultra-Compact Toolbar Row */ }
      <div
          className='overflow-x-auto px-1.5 py-1.5 scrollbar-hide sm:px-2'
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className='flex min-w-max flex-nowrap items-center gap-1.5'>
          { /* Search Section */ }
          <div className='relative w-[180px] shrink-0 sm:w-[240px] lg:w-[280px]'>
            <div className='absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none'>
              <ToolbarSearchIcons className='h-3 w-3 text-slate-400' />
            </div>
            <input
                type='text'
                placeholder='Search...'
                value={globalSearchTerm}
                onChange={(e) => setGlobalSearchTerm(e.target.value)}
                className='h-8 w-full rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-7 text-xs transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-2 focus:ring-emerald-500/15'
                aria-label='Search candidates'
            />
            { globalSearchTerm && (
              <button
                  onClick={clearSearchTerm}
                  className='absolute inset-y-0 right-0 pr-2 flex items-center text-slate-400 hover:text-red-500 transition-colors'
                  title='Clear search'
              >
                <ToolbarClearIcons className='h-2.5 w-2.5' />
              </button>
            ) }
          </div>

          <div className='h-5 w-px shrink-0 bg-slate-200' />

          { /* Main Controls */ }
          <div className='flex shrink-0 items-center gap-1'>
            { viewMode === 'grid' ? (
              <GridViewControls
                  isAnalyticalFilter={isAnalyticalFilter}
                  isAnalyticsFilterOpen={isAnalyticsFilterOpen}
                  handleAnalyticsFilterOpen={handleAnalyticsFilterOpen}
                  is3dView={is3dView}
                  handleToggle3DView={handleToggle3DView}
                  handleGraphConfig={handleGraphConfig}
                  svgActiveTab={svgActiveTab}
                  view3D={view3D}
                  toggleSvgTab={toggleSvgTab}
                  loading={loading}
                  toggle3DViewWithReset={toggle3DViewWithReset}
                  isAnalytical3D={props.isAnalytical3D}
                  showHeadshots={showHeadshots}
                  handleHeadshotToggle={handleHeadshotToggle}
                  currentTab={currentTab}
                  handleStatusChange={handleStatusChange}
                  handlePreQualFilterOpen={handlePreQualFilterOpen}
                  isPreQualFilterOpen={isPreQualFilterOpen}
                  handleCCAFilterOpen={handleCCAFilterOpen}
                  isCCAFilterOpen={isCCAFilterOpen}
              />
            ) : (
              <TableViewControls handleColumnConfig={handleColumnConfig} />
            ) }

            <RefreshButton onRefresh={handleRefreshData} loading={loading} />
          </div>

          { /* Bulk Actions - Now Inline */ }
          { isUnsuccessfulTab && (
            <>
              <div className='h-5 w-px shrink-0 bg-slate-200' />
              <UnsuccessfulApplicantsActions
                  selectAllUnsuccessful={selectAllUnsuccessful}
                  handleClearSelected={handleClearSelected}
                  isClearDisabled={isClearDisabled}
                  handleSendEmailToSelected={handleSendEmailToSelected}
                  isSendEmailDisabled={isSendEmailDisabled}
                  selectedApplicantCount={selectedApplicantCount}
              />
            </>
          ) }

          <div className='h-5 w-px shrink-0 bg-slate-200' />

          { /* Right Side Actions */ }
          <div className='ml-auto flex shrink-0 items-center gap-1.5'>
            <ExportButton handleExport={handleExport} />
            <div className='h-5 w-px bg-slate-200' />
            <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
          </div>
        </div>
      </div>

      { /* Minimal status info / Legend overlay */ }
      { globalSearchTerm && (
        <div className='flex items-center gap-2 border-t border-slate-100 px-4 pb-2 pt-1'>
          <div className='inline-flex items-center gap-1.5 text-[9px] font-medium text-slate-400'>
            <div
                className={`w-1.5 h-1.5 rounded-full ${
                  filteredCandidateCount > 0 ? 'bg-emerald-500' : 'bg-red-500'
                }`}
            />
            { filteredCandidateCount > 0
              ? `${filteredCandidateCount}/${candidateCount}`
              : '0 results' }
          </div>
        </div>
      ) }
    </div>
  );
});

Toolbar.displayName = 'Toolbar';

export default Toolbar;

